import type { ReactElement } from 'react'
import { LinkButtonContextMenu } from '../../../../components/infrastructure/LinkButtonContextMenu'
import { MapButtonContextMenu } from '../../../../components/infrastructure/MapButtonContextMenu'
import { CollectionButtonContextMenu } from '../../../../components/infrastructure/CollectionButtonContextMenu'
import type { CaseInfrastructureTabModalsProps } from '../CaseInfrastructureTabModals'

type LinkContextMenuProps = Pick<
  CaseInfrastructureTabModalsProps,
  | 'selectionCount'
  | 'contextMenu'
  | 'setContextMenu'
  | 'setViewLinksEndpoint'
  | 'setLinkingEndpoint'
  | 'setBulkLinkSourceEndpoint'
  | 'setCopyLinksTargetEndpoint'
  | 'setCopyError'
  | 'getRelationshipCount'
>

export function LinkContextMenuOverlay(props: LinkContextMenuProps): ReactElement | null {
  const {
    selectionCount,
    contextMenu,
    setContextMenu,
    setViewLinksEndpoint,
    setLinkingEndpoint,
    setBulkLinkSourceEndpoint,
    setCopyLinksTargetEndpoint,
    setCopyError,
    getRelationshipCount,
  } = props
  if (!contextMenu) return null
  return (
    <LinkButtonContextMenu
      x={contextMenu.x}
      y={contextMenu.y}
      hasSelection={selectionCount > 0}
      selectionCount={selectionCount}
      relationshipCount={getRelationshipCount(contextMenu.endpoint.id)}
      onClose={() => {
        setContextMenu(null)
      }}
      onViewAllLinks={() => {
        setViewLinksEndpoint(contextMenu.endpoint)
      }}
      onCreateLink={() => {
        setLinkingEndpoint(contextMenu.endpoint)
      }}
      onBulkCreateLink={() => {
        setBulkLinkSourceEndpoint(contextMenu.endpoint)
      }}
      onCopyLinksFrom={() => {
        setCopyLinksTargetEndpoint(contextMenu.endpoint)
        setCopyError(null)
        setContextMenu(null)
      }}
    />
  )
}

type MapMenuProps = Pick<
  CaseInfrastructureTabModalsProps,
  | 'selectionCount'
  | 'mapContextMenu'
  | 'setMapContextMenu'
  | 'mapSetCount'
  | 'isInSet'
  | 'toggleMapSet'
  | 'clearMapSet'
  | 'setShowViewMapSetModal'
  | 'handleAddSelectedToMapSet'
>

export function MapContextMenuOverlay(props: MapMenuProps): ReactElement | null {
  const {
    selectionCount,
    mapContextMenu,
    setMapContextMenu,
    mapSetCount,
    isInSet,
    toggleMapSet,
    clearMapSet,
    setShowViewMapSetModal,
    handleAddSelectedToMapSet,
  } = props
  if (!mapContextMenu) return null
  return (
    <MapButtonContextMenu
      x={mapContextMenu.x}
      y={mapContextMenu.y}
      isInMapSet={isInSet(mapContextMenu.endpoint.id)}
      hasSelection={selectionCount > 0}
      selectionCount={selectionCount}
      mapSetCount={mapSetCount}
      onClose={() => {
        setMapContextMenu(null)
      }}
      onToggle={() => {
        toggleMapSet(mapContextMenu.endpoint.id)
        setMapContextMenu(null)
      }}
      onAddSelected={() => {
        handleAddSelectedToMapSet()
        setMapContextMenu(null)
      }}
      onViewMapSet={() => {
        setShowViewMapSetModal(true)
        setMapContextMenu(null)
      }}
      onClearMapSet={() => {
        void clearMapSet()
        setMapContextMenu(null)
      }}
    />
  )
}

type CollectionMenuProps = Pick<
  CaseInfrastructureTabModalsProps,
  | 'selectionCount'
  | 'collectionContextMenu'
  | 'setCollectionContextMenu'
  | 'collectionSetCount'
  | 'isInCollectionSet'
  | 'toggleCollectionSet'
  | 'clearCollectionSet'
  | 'setShowViewCollectionSetModal'
  | 'handleAddSelectedToCollectionSet'
>

export function CollectionContextMenuOverlay(props: CollectionMenuProps): ReactElement | null {
  const {
    selectionCount,
    collectionContextMenu,
    setCollectionContextMenu,
    collectionSetCount,
    isInCollectionSet,
    toggleCollectionSet,
    clearCollectionSet,
    setShowViewCollectionSetModal,
    handleAddSelectedToCollectionSet,
  } = props
  if (!collectionContextMenu) return null
  return (
    <CollectionButtonContextMenu
      x={collectionContextMenu.x}
      y={collectionContextMenu.y}
      isInCollectionSet={isInCollectionSet(collectionContextMenu.endpoint.id)}
      hasSelection={selectionCount > 0}
      selectionCount={selectionCount}
      collectionSetCount={collectionSetCount}
      onClose={() => {
        setCollectionContextMenu(null)
      }}
      onToggle={() => {
        toggleCollectionSet(collectionContextMenu.endpoint.id)
        setCollectionContextMenu(null)
      }}
      onAddSelected={() => {
        handleAddSelectedToCollectionSet()
        setCollectionContextMenu(null)
      }}
      onViewCollectionSet={() => {
        setShowViewCollectionSetModal(true)
        setCollectionContextMenu(null)
      }}
      onClearCollectionSet={() => {
        clearCollectionSet()
        setCollectionContextMenu(null)
      }}
    />
  )
}
